/**
 * Flags de runtime del programa intake — permiten prender/apagar el intake
 * schema-driven y el chat por mercado **sin rebuild ni redeploy**.
 *
 * Hasta acá el único gate eran las `REACT_APP_INTAKE_*`, que CRA hornea en el bundle:
 * cambiar una exigía pasar por el pipeline entero. Este módulo lee `flags.json`
 * (servido junto al build) al bootear y expone overrides que `pathConfig` consulta
 * antes que el env.
 *
 * **Contrato de seguridad — fail-open.** Si el archivo no está, tarda, devuelve 404 o
 * trae JSON inválido, no hay overrides y cada feature queda exactamente como la dejó su
 * env var compilada. El sistema de flags nunca puede apagar algo por accidente; para
 * apagar hay que decirlo explícitamente.
 *
 * Valores por flag:
 *   'on'   → habilitada (pisa el env)
 *   'off'  → cierra entradas nuevas; las sesiones ya iniciadas terminan su flujo
 *   'kill' → apagado duro: ignora también las sesiones ya iniciadas (emergencia)
 *   'env'  → sin override; manda la env var compilada (valor commiteado por default)
 */
import { REACT_APP_COUNTRY } from '../config';

export type RuntimeFlagValue = 'on' | 'off' | 'kill';
export type RuntimeFlagKey = 'intakeSchemaPath' | 'intakeChat';
export type RuntimeFlagOverrides = Partial<Record<RuntimeFlagKey, RuntimeFlagValue>>;

const FLAG_KEYS: RuntimeFlagKey[] = ['intakeSchemaPath', 'intakeChat'];

/**
 * Mercados cuyo path schema-driven está COMPLETO en este build (rutas montadas, summary,
 * post-form). El override de runtime solo puede PRENDER el intake en estos mercados:
 * `/application/summary` existe únicamente en los routers listados acá, así que un `on` por
 * JSON en un mercado sin esa ruta deja al usuario en blanco justo al completar el wizard.
 * `off`/`kill` se aceptan siempre — apagar nunca requiere un mercado listo.
 * W5b: INT entra junto con `/application/summary` en `intRoutes`. BR sigue afuera
 * hasta que su router monte el sumario en el mismo deploy.
 */
const INTAKE_RUNTIME_READY_MARKETS = new Set(['MX', 'INT']);

/**
 * Dónde vive el archivo. Los seis pipelines hacen `aws s3 sync build s3://<bucket-del-mercado>`
 * contra la RAÍZ del bucket, y el router no usa `basename`, así que la app siempre se sirve en
 * `/`. Antes había una escalera de candidatos (`/mx/flags.json`, `PUBLIC_URL`) para una
 * topología que la organización no usa: además de muerta, cada intento fallido costaba su
 * propio timeout, porque el SPA fallback de CloudFront devuelve `index.html` con 200.
 */
const FLAGS_URL = '/flags.json';

/**
 * Tope de espera del boot. Con 600 ms un fetch en frío (TLS + S3 con `max-age=0`, 1,5–2,3 s
 * medidos en stg INT) caía al env horneado y mandaba cotizaciones nuevas a legacy (SO-1572).
 * 3 s cubre ese caso sin dejar el loader colgado si la red está caída.
 */
export const FLAGS_FETCH_TIMEOUT_MS = 3000;

let overrides: RuntimeFlagOverrides = {};
let readyPromise: Promise<void> | null = null;
/** In-flight load only. A settled load must not block a later retry. */
let inflightLoad: Promise<void> | null = null;

function currentMarket(): string {
  return String(REACT_APP_COUNTRY || 'MX').toUpperCase().trim();
}

/**
 * Extrae los overrides del mercado activo. Tolera tanto `{ markets: { MX: {...} } }`
 * como el objeto plano `{ MX: {...} }`, y descarta en silencio cualquier valor que no
 * sea uno de los tres reconocidos ('env' incluido, que justamente significa "sin override").
 * En mercados fuera de `INTAKE_RUNTIME_READY_MARKETS` los `on` se descartan con un warn:
 * runtime solo puede apagar, nunca prender un mercado que el build no soporta.
 */
export function parseRuntimeFlags(raw: unknown, market: string): RuntimeFlagOverrides {
  const parsed: RuntimeFlagOverrides = {};
  if (!raw || typeof raw !== 'object') return parsed;

  const doc = raw as Record<string, unknown>;
  const byMarket = (
    doc.markets && typeof doc.markets === 'object' ? doc.markets : doc
  ) as Record<string, unknown>;

  const section = byMarket[market];
  if (!section || typeof section !== 'object') return parsed;

  const values = section as Record<string, unknown>;
  FLAG_KEYS.forEach((key) => {
    const rawValue = values[key];
    if (typeof rawValue !== 'string') return;
    const value = rawValue.toLowerCase().trim();
    if (value !== 'on' && value !== 'off' && value !== 'kill') {
      // 'env' es legítimo: significa "sin override". Cualquier otra cosa es un typo — el
      // fail-open se mantiene (manda la env var), pero no debe ser invisible.
      if (value !== 'env') {
        console.warn(
          `[intake/runtimeFlags] valor desconocido ${key}='${rawValue}' en flags.json: ` +
            'se ignora (fail-open, manda la env var). Valores válidos: on | off | kill | env.',
        );
      }
      return;
    }
    if (value === 'on' && !INTAKE_RUNTIME_READY_MARKETS.has(market)) {
      console.warn(
        `[intake/runtimeFlags] se ignora ${key}='on' para ${market}: este build no monta ` +
          'las rutas del intake para ese mercado (ver INTAKE_RUNTIME_READY_MARKETS). ' +
          'Prender un mercado nuevo requiere deploy.',
      );
      return;
    }
    parsed[key] = value;
  });

  return parsed;
}

async function fetchFlagsDocument(): Promise<unknown | undefined> {
  const controller = new AbortController();
  // Abort does not reject a fetch stuck before headers (TCP connect, or a dev
  // server holding the request). Browsers often leave that attempt pending for
  // ~10s, and the boot promise — and the empty page — wait with it. The race is
  // what returns. A body that arrives after the cap is ignored (fail-open).
  let timer: ReturnType<typeof setTimeout> | undefined;
  const fetchPromise = (async (): Promise<unknown | undefined> => {
    try {
      const response = await fetch(FLAGS_URL, { cache: 'no-store', signal: controller.signal });
      if (!response.ok) {
        console.warn(
          `[intake/runtimeFlags] flags.json respondió ${response.status}: ` +
            'se usan los valores del build (fail-open).',
        );
        return undefined;
      }
      // Un SPA fallback devuelve index.html con 200; `JSON.parse` lo descarta.
      const parsed = JSON.parse(await response.text());
      return parsed && typeof parsed === 'object' ? parsed : undefined;
    } catch {
      // Timeout, red caída, JSON roto → sin overrides (fail-open).
      return undefined;
    }
  })();
  const timeoutPromise = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      console.warn(
        `[intake/runtimeFlags] flags.json no llegó en ${FLAGS_FETCH_TIMEOUT_MS}ms: ` +
          'se usan los valores del build (fail-open).',
      );
      resolve(undefined);
    }, FLAGS_FETCH_TIMEOUT_MS);
  });
  try {
    return await Promise.race([fetchPromise, timeoutPromise]);
  } finally {
    clearTimeout(timer);
  }
}

/** Reemplaza los overrides en memoria. La usan el loader y los tests. */
export function setRuntimeFlags(next: RuntimeFlagOverrides): void {
  overrides = next;
}

/** Vuelve al estado "sin overrides" (todo decidido por el env). */
export function resetRuntimeFlags(): void {
  overrides = {};
  readyPromise = null;
  inflightLoad = null;
}

/**
 * Espera a la carga en curso. Resuelve de inmediato si nunca se disparó una, así que un
 * consumidor nunca queda colgado esperando algo que no va a pasar.
 *
 * El boot espera esta promesa antes del primer render. El gate y el kill guard también
 * se suspenden acá por si montan con la carga todavía en curso.
 */
export function whenRuntimeFlagsReady(): Promise<void> {
  return readyPromise ?? Promise.resolve();
}

export function getRuntimeFlag(key: RuntimeFlagKey): RuntimeFlagValue | undefined {
  return overrides[key];
}

/**
 * Carga los flags antes del primer render. **Nunca rechaza ni lanza**: ante cualquier
 * problema deja los overrides vacíos y la app arranca con el comportamiento compilado.
 */
export function loadRuntimeFlags(): Promise<void> {
  // index.tsx, the path gate and the kill guard can all ask during the same
  // boot. Sharing the in-flight promise is what keeps flags.json to one GET.
  // After it settles, a retry (attempt > 0) must hit the network again.
  if (inflightLoad) return inflightLoad;

  const run = (async () => {
    try {
      const flagsDocument = await fetchFlagsDocument();
      setRuntimeFlags(flagsDocument ? parseRuntimeFlags(flagsDocument, currentMarket()) : {});
    } catch {
      setRuntimeFlags({});
    }
  })();

  inflightLoad = run;
  readyPromise = run;
  void run.finally(() => {
    if (inflightLoad === run) inflightLoad = null;
  });
  return run;
}
